import { isPageContentFn } from "./Functions";
import {
  ContentResult,
  DraftEnvelope,
  PageContent,
  PageId,
  PageRecord,
} from "./Types";
const base = "/api/cms";
async function request<T>(
  path: string,
  method = "GET",
  body?: unknown
): Promise<ContentResult<T>> {
  try {
    const response = await fetch(base + path, {
      method,
      signal: AbortSignal.timeout(20000),
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || data?.ok !== true || data.value === undefined)
      return {
        ok: false,
        code:
          response.status === 400
            ? "validation"
            : response.status === 409
            ? "conflict"
            : response.status === 401 || response.status === 403
            ? "forbidden"
            : "network",
        message:
          typeof data?.message === "string"
            ? data.message
            : "The content service did not confirm this operation. Your changes have not been discarded.",
      };
    return { ok: true, value: data.value as T };
  } catch {
    return {
      ok: false,
      code: "network",
      message:
        "Cannot reach the content service. Check your connection and try again.",
    };
  }
}
function isRecord(value: unknown, pageId: PageId): value is PageRecord {
  const v = value as PageRecord | undefined;
  return (
    !!v &&
    v.pageId === pageId &&
    v.schemaVersion === 1 &&
    typeof v.revision === "string" &&
    v.revision.length > 0 &&
    typeof v.updatedAt === "string" &&
    Number.isFinite(Date.parse(v.updatedAt)) &&
    isPageContentFn(v.content)
  );
}
export async function getDraft(
  pageId: PageId
): Promise<ContentResult<DraftEnvelope>> {
  const result = await request<DraftEnvelope>(`/pages/${pageId}/draft`);
  if (
    result.ok &&
    (!result.value ||
      !isRecord(result.value.draft, pageId) ||
      !(
        result.value.publishedRevision === null ||
        typeof result.value.publishedRevision === "string"
      ))
  )
    return {
      ok: false,
      code: "unsupported",
      message:
        "Unsupported content response. Editing is disabled to protect existing content.",
    };
  return result;
}
export async function saveDraft(
  pageId: PageId,
  content: PageContent,
  expectedRevision: string
): Promise<ContentResult<PageRecord>> {
  const result = await request<PageRecord>(`/pages/${pageId}/draft`, "PUT", {
    content,
    expectedRevision,
    schemaVersion: 1,
  });
  if (result.ok && !isRecord(result.value, pageId))
    return {
      ok: false,
      code: "unsupported",
      message: "The server did not return a valid saved revision.",
    };
  return result;
}
export async function publish(
  pageId: PageId,
  draftRevision: string,
  expectedPublishedRevision: string | null
): Promise<ContentResult<PageRecord>> {
  const result = await request<PageRecord>(`/pages/${pageId}/publish`, "POST", {
    draftRevision,
    expectedPublishedRevision,
  });
  if (result.ok && !isRecord(result.value, pageId))
    return {
      ok: false,
      code: "unsupported",
      message: "The server did not confirm a valid published revision.",
    };
  return result;
}
export { request as cmsRequest };

export async function uploadMedia(
  file: File
): Promise<ContentResult<{ id: string; url: string }>> {
  if (
    ![
      "image/png",
      "image/jpeg",
      "image/webp",
      "video/mp4",
      "video/webm",
    ].includes(file.type) ||
    file.size > (file.type.startsWith("video/") ? 40 : 5) * 1024 * 1024
  )
    return {
      ok: false,
      code: "validation",
      message:
        "Choose a PNG, JPEG or WebP photo under 5 MB, or an MP4 or WebM video under 40 MB.",
    };
  try {
    const response = await fetch("/api/cms/media", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type },
      body: file,
    });
    const result = await response.json();
    if (
      !response.ok ||
      result.ok !== true ||
      typeof result.value?.id !== "string" ||
      typeof result.value?.url !== "string"
    )
      return {
        ok: false,
        code: "network",
        message:
          result.message ||
          "Upload was not confirmed. Your previous photo or video is unchanged.",
      };
    return result;
  } catch {
    return {
      ok: false,
      code: "network",
      message: "Upload failed. Your previous photo or video is unchanged.",
    };
  }
}
